import Typography from "@mui/material/Typography";
import type {Editor} from "@tiptap/react";
import React, {useCallback, useMemo, useSyncExternalStore} from "react";
import {useTranslation} from "react-i18next";
import {useRichTextActiveEditor} from "../../common/newRichText";
import {VARIABLE_NODE_NAME, VariableDetailPanel} from "../../common/newVariablePicker";
import {useVariableCatalogue} from "../../common/newVariablePicker/chip";
import {selectedVariable, type SelectedVariable} from "../../common/newVariablePicker/editor";
import {PanelSection} from "./SurveyPanelSection";

export interface SurveySelectedVariable {
    editor: Editor
    selected: SelectedVariable
}

/** Everything the section shows, as one string: equal selections compare equal, so React renders once. */
const selectionSnapshot = (editor: Editor | null) => {
    const selected = editor && !editor.isDestroyed ? selectedVariable(editor) : null;
    return selected ? JSON.stringify(selected) : '';
};

/**
 * The chip selected in the field the author last worked in, while that field can be edited.
 *
 * Its own subscription rather than tiptap's `useEditorState`, which is built around one editor and
 * can keep answering for the previous field after the author moves to the next one.
 */
export function useSurveySelectedVariable(): SurveySelectedVariable | null {
    const editor = useRichTextActiveEditor();

    const subscribe = useCallback((listener: () => void) => {
        if (!editor) return () => undefined;

        editor.on('transaction', listener);
        editor.on('update', listener);
        return () => {
            editor.off('transaction', listener);
            editor.off('update', listener);
        };
    }, [editor]);
    const snapshot = useCallback(() => selectionSnapshot(editor), [editor]);
    const current = useSyncExternalStore(subscribe, snapshot, snapshot);

    return useMemo(() => {
        const selected = current ? JSON.parse(current) as SelectedVariable : null;
        return editor && selected?.editable ? {editor, selected} : null;
    }, [editor, current]);
}

/**
 * The side panel's view of a selected chip: what it shows and the default a guest reads when the
 * value is missing. A survey's chip needs that default, so an empty one is called out here — the
 * publish check refuses it later, this says why now.
 */
function SurveyVariableSection({editor, selected}: SurveySelectedVariable) {
    const {t} = useTranslation();
    const catalogue = useVariableCatalogue(editor);
    const variable = catalogue.variables.find(entry => entry.key === selected.key) ?? null;

    return (
        <PanelSection title={t('Variable')}>
            {variable ? (
                <VariableDetailPanel
                    variable={variable}
                    value={{fallback: selected.fallback, format: selected.format}}
                    // Re-asserting the selection keeps this section open while the Default field is typed in.
                    onChange={next => editor
                        .chain()
                        .updateAttributes(VARIABLE_NODE_NAME, next)
                        .setNodeSelection(selected.pos)
                        .run()}
                    preview={catalogue.preview}
                />
            ) : (
                <Typography variant={'body2'} color={'text.secondary'}>
                    {t('This variable is not available for this survey type.')}
                </Typography>
            )}

            {!!variable && !selected.fallback.trim() && (
                <Typography variant={'body2'} color={'error'}>
                    {t('Required - without it the guest reads a gap in the sentence')}
                </Typography>
            )}
        </PanelSection>
    );
}

export default SurveyVariableSection;
